// Conversation history service using backend proxy
import { Message, Agent } from '../types';
import OidcAuthService from './oidcAuth';

// Backend API configuration - use runtime env
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface ConversationHistory {
  id: string;
  title: string;
  agentId: string;
  agentName: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  isTemporary?: boolean;
}

export interface ConversationSummary {
  id: string;
  title: string;
  agentId: string;
  agentName: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastUserMessage?: string;
  lastAgentMessage?: string;
  messages?: Message[]; // Optional - only included when preloading
}

class ConversationHistoryService {
  private userEmail: string | null = null;
  
  // Helper function to get authentication headers
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const oidcService = OidcAuthService.getInstance();
    
    try {
      await oidcService.ensureInitialized();
      // Prefer ID token (contains app roles)
      let token = await oidcService.getIdToken();
      
      // Fall back to access token if ID token missing
      if (!token) {
        token = await oidcService.getAccessToken();
      }
      
      if (token) {
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }
    
    return { 'Content-Type': 'application/json' };
  }
  
  // Initialize with user email
  async initialize(userEmail: string): Promise<void> {
    // console.log('Initializing conversation history service with email:', userEmail);
    this.userEmail = userEmail;
  }
  
  // Save conversation to backend
  async saveConversation(
    messages: Message[],
    agent: Agent,
    conversationId?: string
  ): Promise<ConversationHistory> {
    if (!this.userEmail) {
      throw new Error('Service not initialized with user email');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/api/conversation-history/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages,
          agent,
          conversationId,
          userEmail: this.userEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save conversation');
      }

      const data = await response.json();
      return data.conversation;
    } catch (error) {
      console.error('Error saving conversation:', error);
      throw new Error('Failed to save conversation history');
    }
  }
  
  // Load conversation from backend
  async getConversation(conversationId: string): Promise<ConversationHistory | null> {
    if (!this.userEmail) {
      throw new Error('Service not initialized with user email');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(
        `${API_BASE_URL}/api/conversation-history/load/${conversationId}?userEmail=${encodeURIComponent(this.userEmail)}`,
        {
          method: 'GET',
          headers
        }
      );

      if (response.status === 404) {
        return null; // Conversation not found
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load conversation');
      }

      const data = await response.json();
      
      // Parse timestamps back to Date objects for messages
      data.conversation.messages = data.conversation.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));

      return data.conversation;
    } catch (error) {
      console.error('Error loading conversation:', error);
      return null;
    }
  }
  
  // List all conversations for the user
  async listConversations(options?: { 
    includeMessages?: boolean;
    limit?: number;
  }): Promise<ConversationSummary[]> {
    if (!this.userEmail) {
      throw new Error('Service not initialized with user email');
    }

    const includeMessages = options?.includeMessages ?? false;
    const limit = options?.limit ?? 50;

    console.log(`Fetching conversations for user: ${this.userEmail} (includeMessages: ${includeMessages}, limit: ${limit})`);
    
    try {
      const headers = await this.getAuthHeaders();
      
      const params = new URLSearchParams({
        userEmail: this.userEmail,
        limit: limit.toString(),
        includeMessages: includeMessages.toString()
      });
      
      const url = `${API_BASE_URL}/api/conversation-history/list?${params}`;
      console.log('Requesting conversations from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to fetch conversations:', errorData);
        throw new Error(errorData.error || 'Failed to list conversations');
      }

      const data = await response.json();
      console.log(`Conversations response: ${data.conversations?.length || 0} conversations` + 
                  (includeMessages ? ' with messages preloaded' : ''));
      
      // Parse message timestamps if messages are included
      if (includeMessages && data.conversations) {
        data.conversations = data.conversations.map((conv: ConversationSummary) => ({
          ...conv,
          messages: conv.messages?.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
      }
      
      return data.conversations || [];
    } catch (error) {
      console.error('Error listing conversations:', error);
      return [];
    }
  }
  
  // Delete conversation from backend
  async deleteConversation(conversationId: string): Promise<boolean> {
    if (!this.userEmail) {
      throw new Error('Service not initialized with user email');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(
        `${API_BASE_URL}/api/conversation-history/delete/${conversationId}?userEmail=${encodeURIComponent(this.userEmail)}`,
        {
          method: 'DELETE',
          headers
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete conversation');
      }

      return true;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return false;
    }
  }
  
  // Clear all conversations for the current user
  async clearAllConversations(): Promise<boolean> {
    if (!this.userEmail) {
      throw new Error('Service not initialized with user email');
    }

    console.log('🗑️ clearAllConversations: Starting clear all conversations for user:', this.userEmail);

    try {
      const headers = await this.getAuthHeaders();
      
      // Try the new bulk clear endpoint first
      const response = await fetch(
        `${API_BASE_URL}/api/conversation-history/clear-all?userEmail=${encodeURIComponent(this.userEmail)}`,
        {
          method: 'DELETE',
          headers
        }
      );

      console.log('🗑️ clearAllConversations: Response status:', response.status);

      if (response.status === 404) {
        // Route doesn't exist yet - use fallback method
        console.log('🗑️ clearAllConversations: Bulk endpoint not available, using fallback method');
        return await this.clearAllConversationsFallback();
      }

      if (!response.ok) {
        const errorData = await response.json();
        console.error('🗑️ clearAllConversations: Backend error:', errorData);
        throw new Error(errorData.error || 'Failed to clear all conversations');
      }

      const data = await response.json();
      console.log('🗑️ clearAllConversations: Success response:', data);
      console.log(`✅ Successfully cleared ${data.deletedCount} conversations using bulk method`);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        // Network error hitting 404 - use fallback
        console.log('🗑️ clearAllConversations: Network 404, using fallback method');
        return await this.clearAllConversationsFallback();
      }
      console.error('🗑️ clearAllConversations: Error clearing all conversations:', error);
      return false;
    }
  }

  // Fallback method: Delete conversations one by one
  private async clearAllConversationsFallback(): Promise<boolean> {
    console.log('🗑️ clearAllConversationsFallback: Getting conversation list');
    
    try {
      // Get list of all conversations
      const conversations = await this.listConversations();
      console.log(`🗑️ clearAllConversationsFallback: Found ${conversations.length} conversations to delete`);
      
      if (conversations.length === 0) {
        console.log('🗑️ clearAllConversationsFallback: No conversations to delete');
        return true;
      }

      // Delete each conversation individually
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const conversation of conversations) {
        try {
          const success = await this.deleteConversation(conversation.id);
          if (success) {
            deletedCount++;
            console.log(`🗑️ clearAllConversationsFallback: Deleted conversation ${conversation.id}`);
          } else {
            failedCount++;
            console.warn(`🗑️ clearAllConversationsFallback: Failed to delete conversation ${conversation.id}`);
          }
        } catch (error) {
          failedCount++;
          console.error(`🗑️ clearAllConversationsFallback: Error deleting conversation ${conversation.id}:`, error);
        }
      }

      console.log(`🗑️ clearAllConversationsFallback: Completed - deleted: ${deletedCount}, failed: ${failedCount}`);
      return deletedCount > 0; // Success if we deleted at least one conversation
    } catch (error) {
      console.error('🗑️ clearAllConversationsFallback: Error in fallback method:', error);
      return false;
    }
  }
  
  // Update existing conversation
  async updateConversation(conversationId: string, messages: Message[], agent: Agent): Promise<ConversationHistory | null> {
    // Update is the same as save with existing conversation ID
    return await this.saveConversation(messages, agent, conversationId);
  }
  
  // Check if service is initialized
  isInitialized(): boolean {
    return this.userEmail !== null;
  }
}

export const conversationHistoryService = new ConversationHistoryService();
export default conversationHistoryService; 